import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PlusOutlined, UnlockOutlined } from '@ant-design/icons';
import { Button, Checkbox, Modal, Select, Spin } from 'antd';
import { AppBarCMS } from '../../../../../components/basic/index.jsx';
import {
  CollapseBlock,
  Spinner,
} from '../../../../../components/common/index.jsx';
import TableFilter from '../../../../../components/common/TableFilter/TableFilter.jsx';
import IngProjectsActiveStudentsTable from './IngProjectsActiveStudentsTable.jsx';
import StudentApplicationsModal from './StudentApplicationsModal.jsx';
import {
  useGetActiveStudentsQuery,
  useGetAcceptedStudentsQuery,
  useDeleteAcceptedStudentMutation,
  useDeleteActiveStudentAssignmentMutation,
  useGetLockedStudentsQuery,
  useGetStudentApplicationsQuery,
  useUnlockStudentMutation,
} from '../../../../../redux/services/ing_projects.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import { ROLE_ING_PROJECTS_ADMIN } from '../../../../../utils/constants/roles.js';
import { ingProjectsApi } from '../../../../../redux/services/ing_projects.jsx';

const IngProjectsActiveStudentsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const VIEW_MODE_STORAGE_KEY = 'cms-ing-projects-students-view-mode';

  const {
    deleteSuccess: showDeleteSuccess,
    successMessage,
    errorMessage,
  } = useToastMessages();
  const [viewMode, setViewMode] = useState('current');
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [resetProjects, setResetProjects] = useState(false);
  const [studentUnlockSearch, setStudentUnlockSearch] = useState('');
  const [applicationsStudent, setApplicationsStudent] = useState(null);
  const isArchiveView = viewMode === 'archive';
  const isAcceptedView = viewMode === 'accepted';
  const isIngProjectsAdmin = useUserHasRole([ROLE_ING_PROJECTS_ADMIN]);
  const availableViewModes = useMemo(
    () => [
      { label: t('Súčasní študenti'), value: 'current' },
      ...(isIngProjectsAdmin
        ? [{ label: t('Prijatí študenti'), value: 'accepted' }]
        : []),
      { label: t('Archív študentov'), value: 'archive' },
    ],
    [t, isIngProjectsAdmin],
  );

  useEffect(() => {
    const storedViewMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    const allowedViewModes = isIngProjectsAdmin
      ? ['current', 'accepted', 'archive']
      : ['current', 'archive'];

    if (allowedViewModes.includes(storedViewMode)) {
      setViewMode(storedViewMode);
    }
  }, [isIngProjectsAdmin]);

  useEffect(() => {
    const allowedViewModes = isIngProjectsAdmin
      ? ['current', 'accepted', 'archive']
      : ['current', 'archive'];

    if (!allowedViewModes.includes(viewMode)) {
      setViewMode('current');
    }
  }, [isIngProjectsAdmin, viewMode]);

  useEffect(() => {
    dispatch(
      ingProjectsApi.util.prefetch('getActiveStudents', 'current', {
        force: true,
      }),
    );
    dispatch(
      ingProjectsApi.util.prefetch('getActiveStudents', 'archive', {
        force: true,
      }),
    );
    if (isIngProjectsAdmin) {
      dispatch(
        ingProjectsApi.util.prefetch('getAcceptedStudents', undefined, {
          force: true,
        }),
      );
    }
    dispatch(
      ingProjectsApi.util.prefetch('getLockedStudents', undefined, {
        force: true,
      }),
    );
  }, [dispatch, isIngProjectsAdmin]);

  const { data: activeStudents, isLoading: isLoadingActiveStudents } =
    useGetActiveStudentsQuery(viewMode, {
      skip: isAcceptedView && isIngProjectsAdmin,
      refetchOnMountOrArgChange: true,
    });
  const {
    data: acceptedStudents,
    isLoading: isLoadingAcceptedStudents,
    refetch: refetchAcceptedStudents,
  } = useGetAcceptedStudentsQuery(undefined, {
    skip: !isAcceptedView || !isIngProjectsAdmin,
    refetchOnMountOrArgChange: true,
  });
  const {
    data: lockedStudents,
    isLoading: isLoadingLockedStudents,
    refetch: refetchLockedStudents,
  } = useGetLockedStudentsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: studentApplications, isLoading: isLoadingStudentApplications } =
    useGetStudentApplicationsQuery(applicationsStudent?.student_id, {
      skip: !applicationsStudent?.student_id,
      refetchOnMountOrArgChange: true,
    });
  const [unlockStudent, { isLoading: isUnlocking }] =
    useUnlockStudentMutation();
  const [
    deleteIngProjectStudents,
    { isSuccess: isDeleteSuccess, reset: resetDelete },
  ] = useDeleteActiveStudentAssignmentMutation();
  const [
    deleteAcceptedStudent,
    { isSuccess: isDeleteAcceptedSuccess, reset: resetDeleteAccepted },
  ] = useDeleteAcceptedStudentMutation();

  useEffect(() => {
    if (isDeleteSuccess) {
      showDeleteSuccess();
      resetDelete(); // prevent endless toasts
    }
  }, [isDeleteSuccess, showDeleteSuccess, resetDelete]);

  useEffect(() => {
    if (isDeleteAcceptedSuccess) {
      showDeleteSuccess();
      resetDeleteAccepted();
    }
  }, [isDeleteAcceptedSuccess, showDeleteSuccess, resetDeleteAccepted]);

  const handleEditClick = (row) => {
    navigate(`${routes.cmsIngPorjectsActiveStudents}/${row.id}/edit`);
  };

  const handleViewApplicationsClick = (row) => {
    if (!row?.student_id) {
      return;
    }

    setApplicationsStudent({
      student_id: row.student_id,
      full_name: row.full_name,
    });
  };

  const handleDeleteAcceptedStudent = async (acceptedStudent) => {
    await deleteAcceptedStudent(acceptedStudent.id).unwrap();
    await refetchAcceptedStudents();
  };

  const handleUnlockStudent = async () => {
    if (selectedStudentIds.length === 0) {
      return;
    }

    try {
      const result = await unlockStudent({
        studentIds: selectedStudentIds,
        resetProjects,
      }).unwrap();

      const wasLockedCount = result?.data?.was_locked_count ?? 0;
      const deletedCount = result?.data?.deleted_applications_count ?? 0;
      const message = wasLockedCount > 0
        ? (wasLockedCount === 1
          ? t('Študent bol odblokovaný.')
          : t('Študenti boli odblokovaní.'))
        : t('Študent nebol zablokovaný.');

      successMessage({
        message,
        description:
          resetProjects && deletedCount > 0
            ? t('Vymazaných žiadostí o projekt: ') + deletedCount
            : resetProjects
              ? t('Neboli vymazané žiadne žiadosti o projekt.')
              : undefined,
      });
      setSelectedStudentIds([]);
      setResetProjects(false);
      setUnlockModalOpen(false);
      await Promise.all([
        refetchLockedStudents(),
        dispatch(
          ingProjectsApi.util.invalidateTags(['IngProjectsStudentStatus']),
        ),
      ]);
    } catch (error) {
      errorMessage({
        message: t('Odblokovanie študenta nebolo úspešné'),
        description: error?.data?.message || t('Nastala chyba.'),
      });
    }
  };

  const studentUnlockOptions = useMemo(
    () =>
      Array.isArray(lockedStudents)
        ? [...lockedStudents]
            .filter((student, index, students) =>
              index === students.findIndex((item) => Number(item.student_id) === Number(student.student_id)),
            )
            .sort((a, b) => {
              const aLastName = (a.full_name || '').trim().split(/\s+/).slice(-1)[0] || '';
              const bLastName = (b.full_name || '').trim().split(/\s+/).slice(-1)[0] || '';

              return aLastName.localeCompare(bLastName, 'sk');
            })
            .map((student) => ({
              value: student.student_id,
              label: `${(() => {
                const parts = (student.full_name || '').trim().split(/\s+/).filter(Boolean);
                if (parts.length <= 1) {
                  return student.full_name || '';
                }
                return `${parts[parts.length - 1]} ${parts.slice(0, -1).join(' ')}`;
              })()} (${student.study_group || student.studyGroup || student.school_group || '-'})`,
              searchLabel: `${student.full_name || ''} ${student.study_group || student.studyGroup || student.school_group || ''}`.trim(),
            }))
        : [],
    [lockedStudents],
  );

  const filteredStudentUnlockOptions = useMemo(() => {
    const searchValue = studentUnlockSearch.trim().toLowerCase();

    if (!searchValue) {
      return studentUnlockOptions;
    }

    return studentUnlockOptions.filter((option) =>
      (option.searchLabel ?? option.label ?? '').toLowerCase().includes(searchValue),
    );
  }, [studentUnlockOptions, studentUnlockSearch]);

  const handleUnlockSelectionChange = (values) => {
    setSelectedStudentIds(values);
  };

  const mainActions = [
    ...(isIngProjectsAdmin
      ? [
          {
            title: t('Priradiť nového študenta na projekt'),
            icon: <PlusOutlined />,
            onClick: () => navigate(routes.cmsIngProjectStudentsAdd),
          },
          {
            title: t('Odblokovať študenta'),
            icon: <UnlockOutlined />,
            onClick: () => setUnlockModalOpen(true),
          },
        ]
      : []),
  ];

  const students =
    isAcceptedView && isIngProjectsAdmin ? acceptedStudents : activeStudents;
  const isStudentsLoading = isAcceptedView
    ? isIngProjectsAdmin
      ? isLoadingAcceptedStudents
      : isLoadingActiveStudents
    : isLoadingActiveStudents;

  if (isStudentsLoading) {
    return <Spinner />;
  }

  return (
    <>
      <AppBarCMS
        title={t('Študenti inž. projektov')}
        backUrl={routes.cmsIngProjects}
        backUrlTooltip={t('Projekty inž. štúdia')}
        mainActions={mainActions}
      />

      <Modal
        open={unlockModalOpen}
        onCancel={() => {
          setUnlockModalOpen(false);
          setSelectedStudentIds([]);
          setResetProjects(false);
          setStudentUnlockSearch('');
        }}
        title={t('Odblokovanie uzamknutia prihlášky študenta')}
        styles={{ footer: { display: 'none' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 12 }}>
          <Select
            style={{ width: '100%' }}
            placeholder={t('Vyberte študenta')}
            value={selectedStudentIds}
            onChange={handleUnlockSelectionChange}
            options={filteredStudentUnlockOptions}
            mode="multiple"
            showSearch
            filterOption={false}
            onSearch={setStudentUnlockSearch}
            onBlur={() => setStudentUnlockSearch('')}
            notFoundContent={
              isLoadingLockedStudents ? <Spin size="small" /> : null
            }
          />
          <Checkbox
            checked={resetProjects}
            onChange={(e) => setResetProjects(e.target.checked)}
          >
            {t('Vymazať súčasne aj projekty, ktoré si študent vybral')}
          </Checkbox>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              onClick={handleUnlockStudent}
              loading={isUnlocking}
              disabled={selectedStudentIds.length === 0 || isUnlocking}
            >
              {t('Odblokovať študenta')}
            </Button>
          </div>
        </div>
      </Modal>

      <StudentApplicationsModal
        isOpen={applicationsStudent !== null}
        onClose={() => setApplicationsStudent(null)}
        applications={studentApplications}
        isLoading={isLoadingStudentApplications}
        studentName={applicationsStudent?.full_name}
      />

      <div style={{ margin: '10px' }}>
        <TableFilter
          title={t('Filter študentov')}
          storageKey={VIEW_MODE_STORAGE_KEY}
          value={viewMode}
          setValue={setViewMode}
          options={availableViewModes}
        />
        <CollapseBlock>
          <IngProjectsActiveStudentsTable
            students={students || []}
            viewMode={viewMode}
            onViewApplicationsClick={handleViewApplicationsClick}
            onEditClick={
              isArchiveView || isAcceptedView ? undefined : handleEditClick
            }
            onDeleteClick={
              isArchiveView
                ? undefined
                : isAcceptedView
                  ? handleDeleteAcceptedStudent
                  : (projectStudnets) => deleteIngProjectStudents(projectStudnets.id)
            }
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default IngProjectsActiveStudentsPage;
